import React, {useEffect, useRef, useState} from "react";
import {debounced} from "../../UtilitiesTs";

const STORAGE_KEY = "image_viewer_panels";

function loadFromStorage(): Record<string, unknown> {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw === null) return {};
        const parsed = JSON.parse(raw);
        return typeof parsed === "object" && parsed !== null ? parsed : {};
    } catch {
        return {};
    }
}

function saveToStorage(data: Record<string, unknown>) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
        console.log(e);
    }
}

export type ImageViewerPanelsSizeContextValue = {
    get: (key: string, defaultValue: string | number) => string | number;
    set: (key: string, value: string | number) => void;
    setDebounced: (key: string, value: string | number) => void;
};

export const ImageViewerPanelsSizeContext = React.createContext<ImageViewerPanelsSizeContextValue | null>(null);


export function ImageViewerPanelsSizeProvider({children}: {children: React.ReactNode}) {
    const [state, setState] = useState<Record<string, unknown>>(loadFromStorage);
    const pendingRef = useRef<Record<string, unknown>>({});

    const debouncedSave = debounced((data: Record<string, unknown>) => {
        setState(data);
        saveToStorage(data);
        pendingRef.current = {};
    }, 200);

    const get = (key: string, defaultValue: string | number): string | number => {
        if (state[key] !== undefined) {
            return state[key] as string | number;
        }
        return defaultValue;
    };

    const set = (key: string, value: string | number) => {
        if (state[key] === value) {
            return;
        }
        const next = {...state, [key]: value};
        saveToStorage(next);
        setState(loadFromStorage());
    };

    const setDebounced = (key: string, value: string | number) => {
        const current = state[key] ?? pendingRef.current[key];
        if (current === value) {
            return;
        }
        const next = {...state, ...pendingRef.current, [key]: value};
        pendingRef.current = next;
        debouncedSave(next);
    };

    useEffect(() => {
        const flush = () => {
            const pending = pendingRef.current;
            if (Object.keys(pending).length > 0) {
                saveToStorage(pending);
            }
        };

        window.addEventListener("beforeunload", flush);

        return () => {
            flush();
            window.removeEventListener("beforeunload", flush);
        };
    }, []);

    return (
        <ImageViewerPanelsSizeContext.Provider value={{get, set, setDebounced}}>
            {children}
        </ImageViewerPanelsSizeContext.Provider>
    );
}
